import { useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { useAppStore } from '@/stores/useAppStore';
import { supabase } from '@/integrations/supabase/client';
import { useDebouncedCallback } from '@/lib/debounce';
import { throttle } from '@/lib/throttle';

/**
 * Hook OTIMIZADO para sincronizar pontuações semanais
 * - Usa Web Worker para cálculos pesados (não bloqueia UI)
 * - Throttling agressivo (max 1x a cada 10s)
 * - Não executa em background (document.hidden)
 * - Debounce de 5s para evitar rajadas
 */
export function useWeeklyScoreSync() {
  const { user } = useAuthStore();
  const { habits, goals } = useAppStore();
  const syncInProgress = useRef(false);
  const workerRef = useRef<Worker | null>(null);
  
  // Inicializar Web Worker uma única vez
  useEffect(() => {
    if (typeof Worker === 'undefined') {
      console.warn('⚠️ Web Workers não suportados, usando modo síncrono');
      return;
    }

    try {
      workerRef.current = new Worker(
        new URL('../workers/scoreCalculator.worker.ts', import.meta.url),
        { type: 'module' }
      );

      // Handler de mensagens do worker
      workerRef.current.onmessage = async (e) => {
        const { type, payload } = e.data;

        if (type === 'SCORES_CALCULATED') {
          // Salvar scores calculados no banco
          try {
            const { userId, weekStart, weekEnd, scores } = payload;

            for (const { pillar, score, metadata } of scores) {
              const { error } = await supabase
                .from('weekly_pillar_scores')
                .upsert({
                  user_id: userId,
                  pillar,
                  week_start_date: weekStart,
                  week_end_date: weekEnd,
                  score,
                  calculation_method: 'intelligent_v2',
                  metadata,
                  updated_at: new Date().toISOString()
                }, {
                  onConflict: 'user_id,pillar,week_start_date'
                });

              if (error) {
                console.error(`❌ Erro ao salvar score do pilar ${pillar}:`, error);
              }
            }

            console.log('✅ Pontuações sincronizadas via Worker');
          } catch (error) {
            console.error('❌ Erro ao salvar pontuações:', error);
          } finally {
            syncInProgress.current = false;
          }
        }
      };

      workerRef.current.onerror = (error) => {
        console.error('❌ Erro no Web Worker:', error);
        syncInProgress.current = false;
      };
    } catch (error) {
      console.error('❌ Erro ao inicializar Worker:', error);
    }

    return () => {
      workerRef.current?.terminate();
    };
  }, []);
  
  // Função de sincronização usando Worker
  const syncScores = useCallback(async () => {
    if (!user || syncInProgress.current || !workerRef.current) return;
    
    // ✅ Não executar em background
    if (document.hidden) {
      console.log('⏭️ Sincronização ignorada (aba em background)');
      return;
    }
    
    try {
      syncInProgress.current = true;
      console.log('🔄 Iniciando sincronização com Worker...');
      
      // 🔥 Filtrar apenas hábitos ativos e metas não arquivadas
      const activeHabits = habits.filter(h => h.active !== false);
      const activeGoals = goals.filter(g => g.status !== 'arquivado');
      
      // Preparar dados
      const today = new Date();
      const monday = getMonday(today);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      
      const weekStart = monday.toISOString().split('T')[0];
      const weekEnd = sunday.toISOString().split('T')[0];
      const weekDates = getWeekDates(monday);
      
      // Enviar para Worker processar (apenas dados ativos)
      workerRef.current.postMessage({
        type: 'CALCULATE_SCORES',
        payload: {
          userId: user.id,
          habits: activeHabits,
          goals: activeGoals,
          weekStart,
          weekEnd,
          weekDates
        }
      });
      
    } catch (error) {
      console.error('❌ Erro ao sincronizar pontuações:', error);
      syncInProgress.current = false;
    }
  }, [user, habits, goals]);
  
  // Debounce de 5 segundos (mais agressivo)
  const debouncedSync = useDebouncedCallback(syncScores, 5000);
  
  // Throttle para máximo 1x a cada 30 segundos (redução de 67% na frequência)
  const throttledSync = useMemo(
    () => throttle(() => {
      if (document.hidden) return; // Não executar em background
      debouncedSync();
    }, 30000, { leading: false, trailing: true }),
    [debouncedSync]
  );
  
  // Detectar mudanças relevantes
  const dataSignature = useMemo(() => {
    return JSON.stringify({
      habitsCount: habits.length,
      goalsCount: goals.length,
      habitsHistory: habits.map(h => h.history?.length || 0).join(','),
      goalsProgress: goals.map(g => 
        g.miniGoals?.filter(m => m.completed).length || 0
      ).join(',')
    });
  }, [habits, goals]);
  
  // Re-calcular quando dados mudarem (com throttle)
  useEffect(() => {
    if (!user) return;
    throttledSync();
  }, [user, dataSignature, throttledSync]);
}

// Helper functions
function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d);
  monday.setDate(diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

function getWeekDates(monday: Date): string[] {
  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    dates.push(date.toISOString().split('T')[0]);
  }
  return dates;
}
